import { supabase } from './supabase.js';
import { DEFAULT_LEAD_SOURCES } from './defaultLeadSources.js';

/**
 * Organization Seeding Service
 * 
 * Handles seeding new organizations with default data like lead sources
 */
class OrganizationSeedingService {
  
  /**
   * Seed an organization with default lead sources
   * @param {string} organizationId - The organization ID to seed
   * @param {string} createdBy - User ID who created the organization
   * @returns {Promise<Array>} Array of created lead sources
   */
  async seedDefaultLeadSources(organizationId, createdBy) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    if (!createdBy) {
      throw new Error('Created by user ID is required');
    }

    try {
      console.log(`🌱 Seeding default lead sources for organization: ${organizationId}`);
      
      // Check if organization already has lead sources
      const { data: existingSources, error: checkError } = await supabase
        .from('lead_sources')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('is_active', true);

      if (checkError) {
        console.error('Error checking existing lead sources:', checkError);
        throw checkError;
      }

      if (existingSources && existingSources.length > 0) {
        console.log(`⚠️  Organization ${organizationId} already has ${existingSources.length} lead sources. Skipping seeding.`);
        return existingSources;
      }

      // Prepare default lead sources for insertion
      const leadSourcesToCreate = DEFAULT_LEAD_SOURCES.map(source => ({
        organization_id: organizationId,
        name: source.name,
        description: source.description,
        created_by: createdBy,
        is_active: true
      }));

      // Insert lead sources in batches to avoid potential limits
      const batchSize = 10;
      const createdSources = [];

      for (let i = 0; i < leadSourcesToCreate.length; i += batchSize) {
        const batch = leadSourcesToCreate.slice(i, i + batchSize);
        
        const { data: batchResult, error: batchError } = await supabase
          .from('lead_sources')
          .insert(batch)
          .select();

        if (batchError) {
          console.error(`Error inserting lead sources batch ${i / batchSize + 1}:`, batchError);
          throw batchError;
        }

        createdSources.push(...(batchResult || []));
      }

      console.log(`✅ Successfully seeded ${createdSources.length} default lead sources for organization ${organizationId}`);
      return createdSources;

    } catch (error) {
      console.error('Error seeding default lead sources:', error);
      throw error;
    }
  }

  /**
   * Add missing default lead sources to an existing organization
   * @param {string} organizationId - The organization ID
   * @param {string} createdBy - User ID who is adding the sources
   * @returns {Promise<Array>} Array of newly created lead sources
   */
  async addMissingDefaultLeadSources(organizationId, createdBy) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    if (!createdBy) {
      throw new Error('Created by user ID is required');
    }

    try {
      console.log(`🔍 Checking for missing default lead sources in organization: ${organizationId}`);
      
      // Get existing lead sources
      const { data: existingSources, error: fetchError } = await supabase
        .from('lead_sources')
        .select('name')
        .eq('organization_id', organizationId)
        .eq('is_active', true);

      if (fetchError) {
        console.error('Error fetching existing lead sources:', fetchError);
        throw fetchError;
      }

      // Find which defaults are missing
      const existingNames = new Set(
        (existingSources || []).map(source => source.name.toLowerCase())
      );

      const missingDefaults = DEFAULT_LEAD_SOURCES.filter(defaultSource => 
        !existingNames.has(defaultSource.name.toLowerCase())
      );

      if (missingDefaults.length === 0) {
        console.log(`✅ Organization ${organizationId} already has all default lead sources`);
        return [];
      }

      console.log(`📝 Found ${missingDefaults.length} missing default lead sources`);

      // Create missing lead sources
      const leadSourcesToCreate = missingDefaults.map(source => ({
        organization_id: organizationId,
        name: source.name,
        description: source.description,
        created_by: createdBy,
        is_active: true
      }));

      const { data: createdSources, error: createError } = await supabase
        .from('lead_sources')
        .insert(leadSourcesToCreate)
        .select();

      if (createError) {
        console.error('Error creating missing lead sources:', createError);
        throw createError;
      }

      console.log(`✅ Successfully added ${createdSources.length} missing default lead sources`);
      return createdSources || [];

    } catch (error) {
      console.error('Error adding missing default lead sources:', error);
      throw error;
    }
  }

  /**
   * Get organizations that don't have default lead sources
   * @returns {Promise<Array>} Array of organization IDs that need seeding
   */
  async getOrganizationsNeedingSeeding() {
    try {
      // Get all organizations
      const { data: organizations, error: orgError } = await supabase
        .from('organizations')
        .select('id, name');

      if (orgError) {
        console.error('Error fetching organizations:', orgError);
        throw orgError;
      }

      const organizationsNeedingSeeding = [];

      // Check each organization
      for (const org of organizations || []) {
        const { data: leadSources, error: sourceError } = await supabase
          .from('lead_sources')
          .select('id')
          .eq('organization_id', org.id)
          .eq('is_active', true);

        if (sourceError) {
          console.error(`Error checking lead sources for org ${org.id}:`, sourceError);
          continue;
        }

        // If organization has fewer than 5 lead sources, it probably needs seeding
        if (!leadSources || leadSources.length < 5) {
          organizationsNeedingSeeding.push(org);
        }
      }

      return organizationsNeedingSeeding;

    } catch (error) {
      console.error('Error getting organizations needing seeding:', error);
      throw error;
    }
  }

  /**
   * Bulk seed all organizations that need default lead sources
   * @param {string} adminUserId - Admin user ID performing the bulk operation
   * @returns {Promise<Object>} Summary of seeding results
   */
  async bulkSeedOrganizations(adminUserId) {
    if (!adminUserId) {
      throw new Error('Admin user ID is required');
    }

    try {
      console.log('🚀 Starting bulk seeding of organizations...');
      
      const organizationsToSeed = await this.getOrganizationsNeedingSeeding();
      
      if (organizationsToSeed.length === 0) {
        console.log('✅ All organizations already have lead sources');
        return { seeded: 0, skipped: 0, errors: [] };
      }

      console.log(`📋 Found ${organizationsToSeed.length} organizations that need seeding`);

      const results = {
        seeded: 0,
        skipped: 0,
        errors: []
      };

      // Seed each organization
      for (const org of organizationsToSeed) {
        try {
          await this.addMissingDefaultLeadSources(org.id, adminUserId);
          results.seeded++;
          console.log(`✅ Seeded organization: ${org.name} (${org.id})`);
        } catch (error) {
          results.errors.push({
            organizationId: org.id,
            organizationName: org.name,
            error: error.message
          });
          console.error(`❌ Failed to seed organization ${org.name}:`, error);
        }
      }

      console.log(`🎉 Bulk seeding completed. Seeded: ${results.seeded}, Errors: ${results.errors.length}`);
      return results;

    } catch (error) {
      console.error('Error in bulk seeding:', error);
      throw error;
    }
  }
}

// Export singleton instance
const organizationSeedingService = new OrganizationSeedingService();
export default organizationSeedingService; 